import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customerAPI, broadbandAPI, catvAPI } from '../services/api';
import toast from 'react-hot-toast';
import { UserPlus, ArrowLeft, ScanLine, Tv, Wifi, LayoutGrid, CheckCircle2, Plus, Check, X } from 'lucide-react';

function SelectWithAdd({ storageKey, value, onChange, placeholder }) {
  const [options, setOptions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  });
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

  const saveNew = () => {
    const trimmed = newVal.trim();
    if (!trimmed) { setAdding(false); setNewVal(''); return; }
    if (!options.includes(trimmed)) {
      const updated = [...options, trimmed];
      setOptions(updated);
      localStorage.setItem(storageKey, JSON.stringify(updated));
    }
    onChange(trimmed);
    setAdding(false);
    setNewVal('');
  };

  const inputStyle = { padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '.88rem', flex: 1 };

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input ref={inputRef} style={inputStyle} value={newVal} onChange={e => setNewVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveNew(); } if (e.key === 'Escape') { setAdding(false); setNewVal(''); } }}
          placeholder={`Type new ${placeholder}...`} />
        <button type="button" onClick={saveNew} style={{ padding: '0 10px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', lineHeight: 0 }}><Check size={14} /></button>
        <button type="button" onClick={() => { setAdding(false); setNewVal(''); }} style={{ padding: '0 10px', background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer', lineHeight: 0 }}><X size={14} /></button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select className="form-control" value={value} onChange={e => onChange(e.target.value)} style={{ flex: 1 }}>
        <option value="">Select {placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <button type="button" onClick={() => setAdding(true)} title={`Add new ${placeholder}`}
        style={{ padding: '0 10px', background: '#f1f5f9', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', lineHeight: 0, flexShrink: 0 }}>
        <Plus size={14} />
      </button>
    </div>
  );
}

const TYPE_OPTIONS = [
  { value: 'catv',      label: 'CATV',      icon: Tv,          color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd', desc: 'Cable TV only' },
  { value: 'broadband', label: 'Broadband', icon: Wifi,        color: '#0891b2', bg: '#cffafe', border: '#67e8f9', desc: 'Internet only' },
  { value: 'both',      label: 'Combo',     icon: LayoutGrid,  color: '#059669', bg: '#d1fae5', border: '#6ee7b7', desc: 'CATV + Broadband' },
];

export default function AddCustomerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledStb = searchParams.get('stb') || '';
  const fromScan     = Boolean(prefilledStb);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    name: '', mobile: '', alternateMobile: '', email: '',
    customerType: fromScan ? 'catv' : 'broadband',
    address: { line1: '', line2: '', area: '', city: 'Ahmedabad', pincode: '', landmark: '' },
    installationDate: new Date().toISOString().split('T')[0],
    broadband: { plan: '', username: '', vlan: '', ispName: '', hardwareName: '', hardwareSerial: '' },
    catv: { package: '', setTopBoxSerial: prefilledStb, entityCode: '', accountNumber: '' },
    securityDeposit: 0, notes: '', createPortalLogin: true,
  });

  useEffect(() => {
    broadbandAPI.getPlans().then(({ data }) => setPlans(data.data)).catch(() => {});
    catvAPI.getPackages().then(({ data }) => setPackages(data.data)).catch(() => {});
  }, []);

  const set = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = { ...obj[keys[i]] };
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (form.customerType === 'catv') delete payload.broadband;
      if (form.customerType === 'broadband') delete payload.catv;
      const { data } = await customerAPI.create(payload);
      toast.success(`Customer created: ${data.data.customerId}`);
      navigate(`/customers/${data.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const showBB = form.customerType === 'broadband' || form.customerType === 'both';
  const showCATV = form.customerType === 'catv' || form.customerType === 'both';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => fromScan ? navigate('/scan') : navigate('/customers')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Add New Customer</h2>
          <p className="text-muted text-small">Fill in the details to register a new customer</p>
        </div>
      </div>

      {fromScan && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 20 }}>
          <ScanLine size={18} color="#ca8a04" />
          <div style={{ fontSize: '.88rem' }}>
            <strong style={{ color: '#92400e' }}>New STB scanned — not found in system</strong>
            <br />
            <span style={{ color: '#78350f' }}>STB Serial <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4 }}>{prefilledStb}</code> has been pre-filled below. Complete the customer details to register.</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Customer Type Selector */}
        <div className="card mb-6">
          <div className="card-header"><h3>Select Customer Type <span className="required">*</span></h3></div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {TYPE_OPTIONS.map(opt => {
                const selected = form.customerType === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('customerType', opt.value)}
                    style={{
                      position: 'relative', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 10, padding: '20px 16px',
                      border: `2px solid ${selected ? opt.color : 'var(--border)'}`,
                      borderRadius: 12, background: selected ? opt.bg : '#fff',
                      cursor: 'pointer', transition: 'all .15s', outline: 'none',
                    }}
                  >
                    {selected && (
                      <CheckCircle2 size={18} color={opt.color} style={{ position: 'absolute', top: 10, right: 10 }} />
                    )}
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: selected ? opt.color : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                      <opt.icon size={22} color={selected ? '#fff' : '#64748b'} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: '.95rem', color: selected ? opt.color : 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid-2">
          {/* Left: Personal Info */}
          <div>
            <div className="card mb-4">
              <div className="card-header"><h3>Personal Information</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Full Name <span className="required">*</span></label>
                  <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Customer full name" />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Mobile <span className="required">*</span></label>
                    <input className="form-control" value={form.mobile} onChange={e => set('mobile', e.target.value)} required placeholder="Primary mobile" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Alternate Mobile</label>
                    <input className="form-control" value={form.alternateMobile} onChange={e => set('alternateMobile', e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Optional" />
                </div>
                <div className="form-group">
                  <label className="form-label">Installation Date <span className="required">*</span></label>
                  <input type="date" className="form-control" value={form.installationDate} onChange={e => set('installationDate', e.target.value)} required />
                </div>
              </div>
            </div>

            <div className="card mb-4" style={{ border: '2px solid var(--primary)', borderRadius: 12 }}>
              <div className="card-header" style={{ background: 'var(--primary)', borderRadius: '10px 10px 0 0' }}>
                <h3 style={{ color: '#fff' }}>Address</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Address <span className="required">*</span></label>
                  <input className="form-control" value={form.address.line1} onChange={e => set('address.line1', e.target.value)} required placeholder="House/Flat no, Street" />
                </div>
                <div className="form-group">
                  <label className="form-label">Society Name <span className="required">*</span></label>
                  <input className="form-control" value={form.address.area} onChange={e => set('address.area', e.target.value)} required placeholder="e.g. Green Park Society, Rose Residency" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Service Info */}
          <div>
            {showBB && (
              <div className="card mb-4">
                <div className="card-header"><h3>Broadband Plan</h3></div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <select className="form-control" value={form.broadband.plan} onChange={e => set('broadband.plan', e.target.value)}>
                      <option value="">Select a plan</option>
                      {plans.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.displayName} — {p.speed.download}/{p.speed.upload} Mbps — ₹{p.price}/mo
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">PPPoE Username</label>
                    <input className="form-control font-mono" value={form.broadband.username} onChange={e => set('broadband.username', e.target.value)} placeholder="PPPoE / radius username" />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">VLAN</label>
                      <SelectWithAdd storageKey="bb_vlan_options" value={form.broadband.vlan} onChange={v => set('broadband.vlan', v)} placeholder="VLAN" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ISP Name</label>
                      <SelectWithAdd storageKey="bb_isp_options" value={form.broadband.ispName} onChange={v => set('broadband.ispName', v)} placeholder="ISP Name" />
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 14 }}>
                    <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Hardware Details</div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Hardware Name</label>
                        <input className="form-control" value={form.broadband.hardwareName} onChange={e => set('broadband.hardwareName', e.target.value)} placeholder="e.g. TP-Link Router" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">S/N Number</label>
                        <input className="form-control font-mono" value={form.broadband.hardwareSerial} onChange={e => set('broadband.hardwareSerial', e.target.value)} placeholder="Serial number" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showCATV && (
              <div className="card mb-4">
                <div className="card-header"><h3>CATV Package</h3></div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Package</label>
                    <select className="form-control" value={form.catv.package} onChange={e => set('catv.package', e.target.value)}>
                      <option value="">Select a package</option>
                      {packages.map(p => (
                        <option key={p._id} value={p._id}>
                          {p.displayName} — ₹{p.price}/mo
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">STB Serial</label>
                    <input className="form-control font-mono" value={form.catv.setTopBoxSerial} onChange={e => set('catv.setTopBoxSerial', e.target.value)} placeholder="Set-top box serial" />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Entity Code</label>
                      <input className="form-control font-mono" value={form.catv.entityCode} onChange={e => set('catv.entityCode', e.target.value)} placeholder="Entity code" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Account Number</label>
                      <input className="form-control font-mono" value={form.catv.accountNumber} onChange={e => set('catv.accountNumber', e.target.value)} placeholder="Account number" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="card mb-4">
              <div className="card-header"><h3>Financial & Notes</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Security Deposit (₹)</label>
                  <input type="number" className="form-control" value={form.securityDeposit} onChange={e => set('securityDeposit', parseInt(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px', background: 'var(--bg)', borderRadius: 6, marginTop: 8 }}>
                  <input type="checkbox" id="portalLogin" checked={form.createPortalLogin} onChange={e => set('createPortalLogin', e.target.checked)} />
                  <label htmlFor="portalLogin" style={{ fontSize: '.85rem', cursor: 'pointer' }}>
                    Create customer portal login (default password: last 4 digits of mobile)
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/customers')}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                <UserPlus size={15} /> {loading ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
